import React, { useState, useEffect} from "react";
import {
  Tabs,
  Tab,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  TextField,
  Grid2,
  MenuItem,
  Select,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import config from '../../config/config.js';
import analyticsConfig from "./analyticsConfig"; // Config file
import Navbar from "../navbar/Navbar"; // Navbar component
import Sidemenu from "../sidemenu/Sidemenu"; // Sidemenu component
import {makeGetRequestToUrl} from '../commonFunc.jsx';
import {useNavigate } from 'react-router-dom';
import exportCSVIcon from '../../assets/icons/exportCSVIcon.svg';

const DynamicAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // Track active tab
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]); // Rows after filtering
  const [searchDriverId, setSearchDriverId] = useState(""); // Track input value for driver_id search
  const [txnTypeFilter, setTxnTypeFilter] = useState("all"); //Dynamic txnType filter 
  const [dateFilter, setDateFilter] = useState("today"); // Default date filter
  const [startDate, setStartDate] = useState(dayjs().startOf("day")); // Set default to start of today
  const [endDate, setEndDate] = useState(dayjs().startOf("day"));
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  // Handle Tab Change
  const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
      setSearchDriverId(""); // Clear the search input
      setFilteredRows(rows); // Reset filtered rows to the original data
      fetchData(newValue); // Fetch data when the tab changes
      setTxnTypeFilter("all");
  };

  // Handle Pagination Change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleRequestSort = (property) => {
    const isAscending = orderBy === property && order === 'asc';
    setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy(property);
    const sortedRows = [...filteredRows].sort((a, b) => {
    if (a[property] < b[property]) return isAscending ? -1 : 1;
    if (a[property] > b[property]) return isAscending ? 1 : -1;
    return 0;
    });
    // setRows(sortedRows);
    setFilteredRows(sortedRows);
};

  // Update start_date and end_date based on selected date filter
  const handleDateFilterChange = (event) => {
    const filter = event.target.value;
    setDateFilter(filter);

    if (filter === "today") {
      console.log("INSIDE TODAY");
      const today = dayjs().startOf("day");
      setStartDate(today);
      setEndDate(today);
    } else if (filter === "thisWeek") {
      const startOfWeek = dayjs().startOf("week").add(1, "day");
      const endOfWeek = dayjs().endOf("week").add(1, "day");
      setStartDate(startOfWeek);
      setEndDate(endOfWeek);
    } else if (filter === "thisMonth") {
      const startOfMonth = dayjs().startOf("month");
      const endOfMonth = dayjs().endOf("month");
      setStartDate(startOfMonth);
      setEndDate(endOfMonth);
    }
  };

  // Handle driver_id search
  const handleSearchDriverId = (event) => {
    const value = event.target.value;
    setSearchDriverId(value);

    const searchKey = analyticsConfig[activeTab]?.searchKey; // Use dynamic search key
    if (!searchKey) {
        console.error("Search key not defined for the active tab.");
        return;
    }

    if (value) {
        const filtered = rows.filter((row) =>
            row[searchKey]?.toString().includes(value) // Ensure safe access
        );
        setFilteredRows(filtered);
    } else {
        setFilteredRows(rows); // Reset if input is cleared
    }
  };

  // Fetch Data for Active Tab
  const fetchData = async (tabIndex) => {
      setLoading(true);

      const config = analyticsConfig[tabIndex];
      const token = localStorage.getItem("token");

      if (!token) {
          console.error("Token is missing");
          setErrorMessage("Your session has expired. Please log in again.");
          setTokenExpired(true);

          setTimeout(() => {
              navigate("/signup");
          }, 2000);
          return;
      }

      // Build dynamic params
      const params = {
          token, // Always include token
          ...(config.params.client_id && { client_id: localStorage.getItem("clientId") }),
          ...(config.params.user_type !== undefined && { user_type: config.params.user_type }),
          ...(config.params.start_date && { start_date: startDate.format("YYYY-MM-DD") }),
          ...(config.params.end_date && { end_date: endDate.format("YYYY-MM-DD") }),
      };

      try {
          const url = config.apiEndpoint;
          const response = await makeGetRequestToUrl(url, params);

          if (response.flag === 102) {
              console.log("Token is expired. Please login again");
              localStorage.removeItem("token");
              setErrorMessage("Your session has expired. Please log in again.");
              setTokenExpired(true);

              setTimeout(() => {
                  navigate("/signup");
              }, 2000);
          }

          setColumns(config.columns);
          setRows(response.data || []);
          setFilteredRows(response.data || []);
      } catch (error) {
          console.error("Error fetching data:", error);
      } finally {
          setLoading(false);
      }
  };

  // Fetch Data on Initial Render
  useEffect(() => {
    fetchData(activeTab);
  }, [startDate, endDate]); // Re-fetch when dates change

  // Export to CSV
  const handleExportCsv = () => {
    const config = analyticsConfig[activeTab];
    const headers = config.columns.map((col) => col.label).join(",");
    const csvRows = filteredRows.map((row) =>
      config.columns.map((col) => `"${row[col.id] || ""}"`).join(",")
    );
    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${config.tabName}.csv`;
    link.click();
  };

  // Handle Transaction Type Filter Change
  const handleTxnTypeFilterChange = (event) => {
    const value = event.target.value;
    setTxnTypeFilter(value);

    // Apply txn_type filter to the rows
    if (value === "all") {
      setFilteredRows(rows); // Show all rows when "All Transactions" is selected
    } else {
      const filtered = rows.filter(
        (row) => row.txn_type === value // Ensure txn_type matches selected filter
      );
      setFilteredRows(filtered);
    }
  };
  
  return (
    // <div>
    //   <Navbar /> 
    //   <div style={{ display: "flex" }}>
    //     <Sidemenu />
        <div style={{ flex: 1}}>
          {/* Main Content */}
          <Typography variant="h4" style={{ marginBottom: "20px" }}>
            Analytics
          </Typography>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={handleTabChange}>
            {analyticsConfig.map((config, index) => (
              <Tab 
                sx={{
                  textTransform:'none', fontSize:'16px', border: '1px solid #D1D5DB', borderRadius:'9px', margin:'0px 8px', width:'200px',
                  color: activeTab==index ? '#fff' : '#83888E', backgroundColor: activeTab==index ? '#0065FF': '#fff','&.Mui-selected': {
                    color: '#fff'
                  },
                }} 
                key={index} label={config.tabName} 
              />
            ))}
          </Tabs> 

          {/* Filters */}
          <Grid2 container spacing={2} style={{ margin: "20px 0" }}>
            {analyticsConfig[activeTab]?.showTxnTypeFilter && (
              <Grid2 item>
                <Select
                  value={txnTypeFilter}
                  onChange={handleTxnTypeFilterChange}
                  style={{
                    width: "200px",height:'50px',
                    backgroundColor: "white",
                    borderRadius: "10px",
                  }}
                >
                  <MenuItem value="all">All Transactions</MenuItem>
                  <MenuItem value="Credited">Credited</MenuItem>
                  <MenuItem value="Debited">Debited</MenuItem>
                </Select>
              </Grid2>
            )}
            <Grid2 item>
              <Select
                value={dateFilter}
                onChange={handleDateFilterChange}
                style={{
                  width: "200px", height:'50px',
                  backgroundColor: "white",
                  borderRadius: "10px",
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </Grid2>
            {dateFilter === "custom" && (
              <>
                <Grid2 item sx={{marginTop:'8px'}}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker sx={{backgroundColor:'#fff'}}
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Grid2>
                <Grid2 item sx={{marginTop:'8px'}}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker sx={{backgroundColor:'#fff'}}
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      style={{ width: "200px", backgroundColor: "white" }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Grid2>
              </>
            )}
            <Grid2 item>
              <input style={{height:'46px', border:'1px solid #D1D5DB', paddingLeft:'12px', fontSize:'15px'}}
                type="number"
                placeholder="User ID"
                className="filterBtn"
                value={searchDriverId}
                onChange={handleSearchDriverId}
              />
            </Grid2>
            <Grid2 item sx={{ marginLeft: "auto" }}>
                <Button
                  variant="contained"
                  onClick={handleExportCsv}
                  sx={{
                      backgroundColor: '#0DBA78', color: '#fff', border: '1px solid #0DBA78', textTransform: 'none',
                      borderRadius: '8px', minWidth:'150px', boxShadow:'none', height: '48px',
                      '&:hover': {backgroundColor: '#0DBA78', color: 'white'},
                      '&:active': { backgroundColor: '#0DBA78', color: 'white'}
                  }}>
                  
                  <Typography sx={{marginRight:'6%'}}>Export CSV</Typography>
                  <img src={exportCSVIcon} alt="exportCSV" />
              </Button>
            </Grid2>

          </Grid2>

          {/* Table */}
          <TableContainer
            style={{
              maxHeight: "700px",
              overflow: "auto",
              border: "1px solid #D1D5DB",
              borderRadius: "16px",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align="center"
                      sx={{
                        backgroundColor: "#1A1E25",
                        color: "#D1D5DB",
                        fontSize: "14px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <TableSortLabel
                          className="table-header"
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : "asc"}
                          onClick={() => handleRequestSort(col.id)}
                          sx={{
                            "& .MuiTableSortLabel-icon": {
                              color: "#D1D5DB !important", // Change the arrow color
                            },
                          }}
                        >
                          {col.label}
                        </TableSortLabel>
                      
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) // Paginate rows
                  .map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#F9F9F9" },
                        "&:hover": { backgroundColor: "#F9F9F9" },

                        "&:nth-of-type(even)": { backgroundColor: "#ffffff" },
                        "&:hover": { backgroundColor: "#ffffff" },
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          align="center"
                          sx={{ 
                            border: "1px solid #D1D5DB", 
                            color: col.label === "Total Earnings"
                                    ? "#0DBA78" // Green for Total Earnings
                                    : col.label === "Transaction Type"
                                    ? row[col.id] === "Credited"
                                      ? "#0DBA78" // Green for Credited
                                      : row[col.id] === "Debited"
                                      ? "#D14545" // Red for Debited
                                      : "inherit" // Default for other values
                                    : col.label === "Amount"
                                    ? row["txn_type"] === "Credited"
                                      ? "#0DBA78" // Green for Amount if Transaction Type is Credited
                                      : row["txn_type"] === "Debited"
                                      ? "#D14545" // Red for Amount if Transaction Type is Debited
                                      : "inherit" // Default for other cases
                                    : "inherit"
                          }}
                        >
                          {row[col.id] || "N/A"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />

          {loading && <Typography>Loading...</Typography>}
        </div>
    //   </div>
    // </div>
  );
};

export default DynamicAnalytics;